import * as api from 'api/hours/hours.api';
import { errorMessageFactory } from 'factories/api';
import { suggestionToHourDraftFactory } from 'factories/suggestions';
import { uniq } from 'lodash-es';
import { clearHourDraft, createHourDraft, saveHourDraft } from 'redux/modules/hours.actions';
import { showToast } from 'redux/modules/toasts.actions';
import { getHourEditorProps } from 'redux/selectors/hours.draft';
import { getSuggestionEntities, getSuggestions } from 'redux/selectors/suggestions';
import type { HourAnalyticsContext } from 'types/analytics';
import type { Thunk } from 'types/redux';
import { fetchFullProjectsIfNeeded } from './projects.actions';
import * as actionTypes from './suggestions.types';
import { wrapFetching, wrapSaving } from './suggestions.types';
import { ensureEntryIdsAreNotMarkedAsSpam } from './timeline.actions';

/**
 * Initiate a new hour draft based on a suggestion's id reference
 */
export function createHourDraftFromSuggestionId(
  id: number,
  context: HourAnalyticsContext
): Thunk<boolean> {
  return (dispatch, getState) => {
    const state = getState();
    const suggestions = getSuggestionEntities(state);
    const suggestion = suggestionToHourDraftFactory(suggestions[id]);
    if (!suggestion) {
      dispatch(showToast({ message: 'Could not open suggestion', type: 'danger' }));
      return false;
    } else {
      dispatch(createHourDraft(context, suggestion));
      return true;
    }
  };
}

export function acceptSuggestionOrOpenDraft(id: number, context: HourAnalyticsContext): Thunk {
  return (dispatch, getState) => {
    const created = dispatch(createHourDraftFromSuggestionId(id, context));
    if (created) {
      const hourEditorProps = getHourEditorProps(getState());
      const canSave = !hourEditorProps.ui.isSaveDisabled;
      if (canSave) {
        // If we can't save, just leave the editor open
        dispatch(saveHourDraft({}, { clearDraft: true }));
        dispatch(clearHourDraft());
      }
    }
  };
}

/**
 * Fetch suggestions for the authenticated user between two dates
 */
export function fetchSuggestions(date: { from: string; to: string }): Thunk<Promise<void>> {
  const { from, to } = date;
  return async dispatch => {
    try {
      const suggestions = await dispatch(wrapFetching(api.getSuggestions(from, to)));
      dispatch(actionTypes.fetchedSuggestions(suggestions));
      dispatch(ensureSuggestionProjectsLoaded());
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

function ensureSuggestionProjectsLoaded(): Thunk {
  return (dispatch, getState) => {
    const suggestionsAfterDispatch = getSuggestions(getState());
    const suggestionProjectIds = uniq(
      suggestionsAfterDispatch.map(suggestion => suggestion.project_id)
    );
    dispatch(fetchFullProjectsIfNeeded(suggestionProjectIds));
  };
}

/**
 * Dismiss suggestion in local state + server side
 */
export function dismissSuggestionId(id: number): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const suggestion = getSuggestionEntities(getState())[id];
    try {
      dispatch(actionTypes.dismissSuggestionId(id));
      await dispatch(wrapSaving(api.dismissSuggestionId(id)));
    } catch (error) {
      dispatch(actionTypes.reAppendSuggestion(suggestion));
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

/**
 * Fetch suggestions by id reference
 */
export function fetchSuggestionIds(ids: Array<number>): Thunk {
  return async dispatch => {
    try {
      const data = await dispatch(wrapFetching(api.getSuggestionIds(ids)));
      dispatch(actionTypes.appendSuggestion(data));
      dispatch(ensureSuggestionProjectsLoaded());
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function updateSuggestionEntries(
  suggestionId: number,
  entryIds: Array<number>
): Thunk<Promise<unknown>> {
  return async dispatch => {
    dispatch(actionTypes.setSuggestionEntries({ suggestionId, entryIds }));
    if (suggestionId < 0) return;

    try {
      const updatedSuggestion = await dispatch(
        wrapFetching(api.updateSuggestionIdEntries(suggestionId, entryIds))
      );
      dispatch(actionTypes.appendSuggestion([updatedSuggestion]));
      dispatch(ensureEntryIdsAreNotMarkedAsSpam(updatedSuggestion.suggested_entry_ids));
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}
